import { Unionize } from 'utilitys/helpers/TsGeneral';
import { StateCreator, create } from 'zustand';

import {
    GlobalFilterConfigItem,
    IHashmap,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { IVersionCompareTableConfig } from 'components/Dashboards/Project/Components/VersionCompare/VersionCompare.consts';

const defaultWidgetState = {
    NoPermissionMessage: '',
    componentKey: '' as keyof IHashmap,
    name: '',
    id: '',
    endpoints: {},
};

interface WidgetConfiguration {
    NoPermissionMessage: string;
    titleWithoutProjectAndContract?: string;
    permission?: keyof IPrivileges | '';
    componentKey: keyof IHashmap;
    elementId?: keyof IVersionCompareTableConfig;
    name: string;
    id: string;
    globalFiltersConfig?: GlobalFilterConfigItem[];
    componentMenu?: {
        useAutomatedNarrative?: boolean;
    };
    routeId?: string;
    defaultSize?: string;
    isResizable?: boolean;
    minH?: number;
    minW?: number;
    maxH?: number;
    maxW?: number;
    endpoints: Record<string, string>;
    reportReady?: boolean;
}

export interface Props {
    criticalPathAdded: WidgetConfiguration;
    criticalityWeightedSCurve: WidgetConfiguration;
    riskHeatMap: WidgetConfiguration;
    topCriticalActivitiesDelayDrivers: WidgetConfiguration;
    topDurationOverrunRisk: WidgetConfiguration;
    progressSummary: WidgetConfiguration;
    topOverdueOverrunActivities: WidgetConfiguration;
    highLevelProgramLatest: WidgetConfiguration;
    topStatusChanges: WidgetConfiguration;
    topStartDateChanges: WidgetConfiguration;
    topFinishDateChanges: WidgetConfiguration;
    largestDurationVariance: WidgetConfiguration;
    largestFloatChanges: WidgetConfiguration;
    topRelationshipsTypeChanges: WidgetConfiguration;
    topRelationshipsCountChanges: WidgetConfiguration;
    largestAddedActivities: WidgetConfiguration;
    largestDeletedActivities: WidgetConfiguration;
    criticalPathRemoved: WidgetConfiguration;
    changeSummary: WidgetConfiguration;
    projectSummary: WidgetConfiguration;
    spiTrend: WidgetConfiguration;
    productivityRiskTrend: WidgetConfiguration;
    scheduleQualityTrend: WidgetConfiguration;
    prioritiesMatrix: WidgetConfiguration;
    keyAchievements: WidgetConfiguration;
    projectScorecards: WidgetConfiguration;
    battlecardsRowsBoard: WidgetConfiguration;
    activityPredecessors: WidgetConfiguration;
    sharedResourcesAvailability: WidgetConfiguration;
    keyActivities: WidgetConfiguration;
    timelineAndDensity: WidgetConfiguration;
    finishDateOverallSummary: WidgetConfiguration;
    finishDateActivitiesPredecessors: WidgetConfiguration;
    productivityRate: WidgetConfiguration;
    resourcesAnalysisChart: WidgetConfiguration;
    trends: WidgetConfiguration;
    customReportText: WidgetConfiguration;
    scheduleQualityScore: WidgetConfiguration;
    currentSpi: WidgetConfiguration;
    scheduleDelay: WidgetConfiguration;
    productivityRiskKpi: WidgetConfiguration;
    outOfSequence: WidgetConfiguration;
    danglingLogic: WidgetConfiguration;
    leads: WidgetConfiguration;
    lags: WidgetConfiguration;
    relationshipTypes: WidgetConfiguration;
    hardConstraints: WidgetConfiguration;
    highFloatByActivityType: WidgetConfiguration;
    negativeFloatByActivityType: WidgetConfiguration;
    longDuration: WidgetConfiguration;
    invalidActualDates: WidgetConfiguration;
    invalidForecastDates: WidgetConfiguration;
    unresourcedActivities: WidgetConfiguration;
    missedActivities: WidgetConfiguration;
    criticalPathTest: WidgetConfiguration;
    criticalPathLengthIndex: WidgetConfiguration;
    baselineExecutionIndex: WidgetConfiguration;
    missingBaselineDates: WidgetConfiguration;
    duplicateRelations: WidgetConfiguration;
    softConstraints: WidgetConfiguration;
    allScheduleQualityChecks: WidgetConfiguration;
    criticalPathCongestion: WidgetConfiguration;
    incompleteWithFinishDate: WidgetConfiguration;
    constraintsWithoutRelationship: WidgetConfiguration;
    sensitiveActivitiesByDuration: WidgetConfiguration;
    workSettingsApplied: WidgetConfiguration;
    startFinishTimeNotRound: WidgetConfiguration;
    activitiesWithEarlyFinishTime: WidgetConfiguration;
    activitiesWithLateStartTime: WidgetConfiguration;
    frequentlyUsedResources: WidgetConfiguration;
    rarelyUsedResources: WidgetConfiguration;
    relationshipsActivitiesRatio: WidgetConfiguration;
    milestonesWeightedSCurve: WidgetConfiguration;
    mySummaryTrackedActivities: WidgetConfiguration;
    myVersionCompareTrackedActivities: WidgetConfiguration;
    topRecommendations: WidgetConfiguration;
    overallStatus: WidgetConfiguration;
    dataStories: WidgetConfiguration;
    phasePerformanceChart: WidgetConfiguration;
    phasePerformanceBreakdown: WidgetConfiguration;
    ganttChart: WidgetConfiguration;
    finishDateFuturePotentialDelayDrivers: WidgetConfiguration;
    changedActivities: WidgetConfiguration;
    resourcesAnalysisTable: WidgetConfiguration;
    productivityTable: WidgetConfiguration;
    riskRegister: WidgetConfiguration;
    automatedNarrative: WidgetConfiguration;
    reportSummary: WidgetConfiguration;
    activitiesPivotTableView: WidgetConfiguration;
    update: (value: Unionize<Omit<Props, 'update'>>) => void;
}

const state: StateCreator<Props> = (set) => ({
    riskHeatMap: defaultWidgetState,
    criticalPathAdded: defaultWidgetState,
    criticalityWeightedSCurve: defaultWidgetState,
    topCriticalActivitiesDelayDrivers: defaultWidgetState,
    topDurationOverrunRisk: defaultWidgetState,
    progressSummary: defaultWidgetState,
    topOverdueOverrunActivities: defaultWidgetState,
    highLevelProgramLatest: defaultWidgetState,
    topStatusChanges: defaultWidgetState,
    topStartDateChanges: defaultWidgetState,
    topFinishDateChanges: defaultWidgetState,
    largestDurationVariance: defaultWidgetState,
    largestFloatChanges: defaultWidgetState,
    topRelationshipsTypeChanges: defaultWidgetState,
    topRelationshipsCountChanges: defaultWidgetState,
    largestAddedActivities: defaultWidgetState,
    largestDeletedActivities: defaultWidgetState,
    criticalPathRemoved: defaultWidgetState,
    changeSummary: defaultWidgetState,
    projectSummary: defaultWidgetState,
    spiTrend: defaultWidgetState,
    productivityRiskTrend: defaultWidgetState,
    scheduleQualityTrend: defaultWidgetState,
    prioritiesMatrix: defaultWidgetState,
    keyAchievements: defaultWidgetState,
    projectScorecards: defaultWidgetState,
    battlecardsRowsBoard: defaultWidgetState,
    activityPredecessors: defaultWidgetState,
    sharedResourcesAvailability: defaultWidgetState,
    keyActivities: defaultWidgetState,
    timelineAndDensity: defaultWidgetState,
    finishDateOverallSummary: defaultWidgetState,
    finishDateActivitiesPredecessors: defaultWidgetState,
    productivityRate: defaultWidgetState,
    resourcesAnalysisChart: defaultWidgetState,
    trends: defaultWidgetState,
    customReportText: defaultWidgetState,
    scheduleQualityScore: defaultWidgetState,
    currentSpi: defaultWidgetState,
    scheduleDelay: defaultWidgetState,
    productivityRiskKpi: defaultWidgetState,
    outOfSequence: defaultWidgetState,
    danglingLogic: defaultWidgetState,
    leads: defaultWidgetState,
    lags: defaultWidgetState,
    relationshipTypes: defaultWidgetState,
    hardConstraints: defaultWidgetState,
    highFloatByActivityType: defaultWidgetState,
    negativeFloatByActivityType: defaultWidgetState,
    longDuration: defaultWidgetState,
    invalidActualDates: defaultWidgetState,
    invalidForecastDates: defaultWidgetState,
    unresourcedActivities: defaultWidgetState,
    missedActivities: defaultWidgetState,
    criticalPathTest: defaultWidgetState,
    criticalPathLengthIndex: defaultWidgetState,
    baselineExecutionIndex: defaultWidgetState,
    missingBaselineDates: defaultWidgetState,
    duplicateRelations: defaultWidgetState,
    softConstraints: defaultWidgetState,
    allScheduleQualityChecks: defaultWidgetState,
    criticalPathCongestion: defaultWidgetState,
    incompleteWithFinishDate: defaultWidgetState,
    constraintsWithoutRelationship: defaultWidgetState,
    sensitiveActivitiesByDuration: defaultWidgetState,
    workSettingsApplied: defaultWidgetState,
    startFinishTimeNotRound: defaultWidgetState,
    activitiesWithEarlyFinishTime: defaultWidgetState,
    activitiesWithLateStartTime: defaultWidgetState,
    frequentlyUsedResources: defaultWidgetState,
    rarelyUsedResources: defaultWidgetState,
    relationshipsActivitiesRatio: defaultWidgetState,
    milestonesWeightedSCurve: defaultWidgetState,
    mySummaryTrackedActivities: defaultWidgetState,
    myVersionCompareTrackedActivities: defaultWidgetState,
    topRecommendations: defaultWidgetState,
    overallStatus: defaultWidgetState,
    dataStories: defaultWidgetState,
    phasePerformanceChart: defaultWidgetState,
    phasePerformanceBreakdown: defaultWidgetState,
    ganttChart: defaultWidgetState,
    finishDateFuturePotentialDelayDrivers: defaultWidgetState,
    changedActivities: defaultWidgetState,
    resourcesAnalysisTable: defaultWidgetState,
    productivityTable: defaultWidgetState,
    riskRegister: defaultWidgetState,
    automatedNarrative: defaultWidgetState,
    reportSummary: defaultWidgetState,
    activitiesPivotTableView: defaultWidgetState,
    update: (configurations) => set(configurations),
});

export const useConfigurationStore = create(state);
